import React, { useEffect, useRef } from 'react';
import { Button } from './Button';
import { PlayIcon, Users, Brain, Target, Star, Heart, BookOpenIcon } from 'lucide-react';
export const Hero = () => {
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const parallaxFast = document.querySelector('.parallax-fast');
      if (parallaxFast) parallaxFast.setAttribute('style', `transform: translateY(${scrollY * 0.02}px)`);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <section className="w-full py-section-mobile md:py-section-desktop overflow-hidden relative bg-gradient-to-br from-white via-primary-50/30 to-blue-50/30" data-id="element-164" data-mp="s9q7xqjvctfg">
      <style jsx data-id="element-165" data-mp="3d21v45faeyrf">{`
        @keyframes hero-breathe-img {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.02);
          }
        }

        @keyframes hero-pulse-glow {
          0%,
          100% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
        }

        .hero-illustration-container {
          position: relative;
          transition: transform 1.5s ease-in-out;
        }

        .hero-illustration-container:hover {
          transform: scale(1.04);
        }

        .hero-illustration-container .hero-img {
          transition: transform 1.5s ease-in-out;
        }

        .hero-illustration-container:hover .hero-img {
          animation: hero-breathe-img 2.5s ease-in-out infinite;
        }

        .hero-illustration-container::before {
          content: '';
          position: absolute;
          inset: -40px;
          background: radial-gradient(
            ellipse at center,
            rgba(251, 146, 60, 0.4) 0%,
            rgba(249, 115, 22, 0.3) 30%,
            rgba(234, 88, 12, 0.2) 50%,
            transparent 70%
          );
          opacity: 0;
          transition: opacity 1.5s ease-in-out;
          pointer-events: none;
          z-index: -1;
          filter: blur(30px);
        }

        .hero-illustration-container:hover::before {
          opacity: 0.6;
          animation: hero-pulse-glow 2.5s ease-in-out infinite;
        }

        .hero-illustration-container::after {
          content: '';
          position: absolute;
          inset: -20px;
          background: radial-gradient(
            circle,
            rgba(6, 182, 212, 0.15) 0%,
            transparent 60%
          );
          opacity: 0;
          transition: opacity 1.5s ease-in-out;
          pointer-events: none;
          z-index: -1;
          filter: blur(20px);
        }

        .hero-illustration-container:hover::after {
          opacity: 0.5;
          animation: hero-pulse-glow 2.5s ease-in-out infinite 0.3s;
        }
      `}</style>

      {/* Background Icons */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-166" data-mp="3hq248rtd3w77">
        {/* Icons with adjusted opacity */}
        <div className="absolute top-20 left-[10%] w-32 h-32 text-gray-300/10 transform -rotate-12" data-id="element-167" data-mp="1x7hw9eyk651e">
          <BookOpenIcon strokeWidth={1} data-id="element-168" data-mp="218jzs7zid396" />
        </div>
        <div className="absolute top-40 right-[15%] w-40 h-40 text-gray-300/10 transform rotate-12" data-id="element-169" data-mp="2697najoj31yn">
          <Users strokeWidth={1} data-id="element-170" data-mp="2b1hjghg8ky9d" />
        </div>
        <div className="absolute bottom-40 left-[20%] w-36 h-36 text-gray-300/10 transform rotate-45" data-id="element-171" data-mp="2fzsswr2md6r2">
          <Brain strokeWidth={1} data-id="element-172" data-mp="2kynajpouop9l" />
        </div>
        <div className="absolute top-1/3 right-[25%] w-28 h-28 text-gray-300/10" data-id="element-173" data-mp="2kxc6rpvenhih">
          <Target strokeWidth={1} data-id="element-174" data-mp="2p1jzrz8wbjmi" />
        </div>
        <div className="absolute bottom-1/4 right-[10%] w-24 h-24 text-gray-300/10 transform -rotate-12" data-id="element-175" data-mp="2oyo1g9t4ysxj">
          <Star strokeWidth={1} data-id="element-176" data-mp="umhd09b8efna" />
        </div>
        <div className="absolute top-2/3 left-[15%] w-32 h-32 text-gray-300/10" data-id="element-177" data-mp="unre77dds3y5">
          <Heart strokeWidth={1} data-id="element-178" data-mp="zkr9cn8ozmah" />
        </div>
      </div>
      <div className="container mx-auto px-4 sm:px-6 xl:px-20 2xl:px-32" data-id="element-179" data-mp="zndr1fpari6f">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center" data-id="element-180" data-mp="49t5s073b8vi">
          <div className="lg:col-span-7 relative" data-id="element-181" data-mp="3vgvwyrmxhf5q">
            <div id="hero-logo" className="mb-8 lg:mb-12" data-id="element-182" data-mp="e9a7y7n4v4mq">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jP7ueorRtM9x7giMYpBsys/nisa-capriola-logotype.png" alt="Nisa" className="h-14 md:h-20 w-auto" data-id="element-183" data-mp="e8se1hny12hz" />
            </div>
            <h1 className="font-serif font-medium text-teal-950 mb-6 lg:mb-10 text-[36px] sm:text-[42px] md:text-[48px] lg:text-display-desktop leading-tight text-balance" data-id="element-184" data-mp="3crpkhmcq9jsd">
              The everyday workspace for instructional coaching
            </h1>
            <p className="text-gray-600 mb-8 max-w-2xl md:leading-normal text-[16px]" data-id="element-185" data-mp="3mo2d0gwjdrlj">
              Nisa helps instructional coaches prepare, reflect, and communicate
              across all their teachers and goals. Chat with Nisa anytime to
              draft follow-ups, plan sessions, and stay organized. For schools,
              it connects that work into a clear view across classrooms and
              helps your team deliver consistent, high-quality coaching to every
              teacher.
            </p>
            <div className="flex flex-col sm:flex-row gap-6" data-id="element-186" data-mp="zin02hvb8ewh">
              <a href="http://app.nisa.coach" data-id="element-187" data-mp="14k3vvyyxtb67">
                <Button size="lg" data-id="element-188" data-mp="1s9eouua1q19s">Create a coach account</Button>
              </a>
            </div>
          </div>
          <div className="lg:col-span-5" data-id="element-189" data-mp="1x8rmma2ggsr5">
            <div className="relative h-full hero-illustration-container" data-id="element-190" data-mp="1x7gk0dkqxr1m">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2Huxx8rff8YtszHy2fW7f1/Nisa_Duo.png" alt="Illustration of a coach and teacher having a conversation" className="hero-img w-full h-full object-contain" data-id="element-191" data-mp="1j7f9e6eoj58y" />
            </div>
          </div>
        </div>
      </div>
    </section>;
};